import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormRow } from '@tanium/react-form';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import { Button } from '@tanium/react-button';
import { Spinner } from '@tanium/react-spinner';
import { ThemeColors } from '@tanium/theme-data';
import { SmallSubtleText } from '../../TextStyles';
import { LoginFormTextInput, LoginFormButton } from './LoginFormComponents';
import { useAsync, AsyncStatus } from '../../hooks/useAsync';
import { TaniumLoginCard } from './TaniumLoginCard';
import { ErrorText } from './ErrorText';
import { useOnInputChange } from '../../components/InputHandlerUtils';
import { AuthCallbacks } from '../../auth/Auth';
import { UnauthenticatedRoutes } from '../../components/UnauthenticatedArea/UnauthenticatedRoutes';
import { SessionKeys } from '../../types';
import { ApiError, parseApiErrorMsg } from '../../hooks/useApiErrorMsgParser';
import { forgetWorkspace, getRememberedWorkspace } from '../../auth/workspaceCookie';

interface WorkspaceSelectionProps {
  auth: AuthCallbacks;
}

export const WorkspaceSelection: React.VFC<WorkspaceSelectionProps> = ({ auth }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [workspace, setWorkspace] = useState('');
  const workspaceNotFound = new URLSearchParams(location.search).get('workspaceNotFound') === 'true';
  const email = sessionStorage.getItem(SessionKeys.Email) || '';
  const [rememberedWorkspace, setRememberedWorkspace] = useState(() => getRememberedWorkspace(email));

  const { status, error, execute } = useAsync((workspaceOverride?: string) => {
    const targetWorkspace = workspaceOverride ?? workspace;
    if (targetWorkspace === '') {
      throw new Error(t('Account identifier is required'));
    }
    return auth.signInWithWorkspace(targetWorkspace, email).catch((err: ApiError) => {
      if (err.response?.status === 404) {
        if (workspaceOverride) {
          forgetWorkspace(email);
          setRememberedWorkspace(null);
        }
        throw new Error(t('Workspace not found'));
      }
      throw new Error(parseApiErrorMsg(t, err));
    });
  });

  const errorMessage = error?.message ?? (status === AsyncStatus.Idle && workspaceNotFound ? t('Workspace not found') : undefined);

  const handleWorkspaceChange = useOnInputChange((value: string) => setWorkspace(value.toLowerCase()));

  const onSubmit = useCallback<React.FormEventHandler>(
    async (event) => {
      event.preventDefault();
      await execute();
    },
    [execute]
  );

  const onCancel = () => {
    sessionStorage.setItem(SessionKeys.Email, '');
    window.location.hash = UnauthenticatedRoutes.LOGIN_ROUTE;
  };

  const handleFindWorkspaces = () => {
    window.location.hash = UnauthenticatedRoutes.WORKSPACE_HELP_ROUTE;
  };

  const isPending = status === AsyncStatus.Pending;

  const workspaceIdentifierInput = (
    <>
      <LoginFormTextInput
        value={workspace}
        placeholder={t('Enter your workspace identifier...')}
        onChange={handleWorkspaceChange}
        required
      />
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </>
  );

  return (
    <Box as={Stack} justifyContent="center" marginTop="150px">
      <TaniumLoginCard>
        <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            style={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {rememberedWorkspace && (
              <>
                <Button
                  type="button"
                  style={{ width: '100%' }}
                  disabled={isPending}
                  onClick={async () => {
                    await execute(rememberedWorkspace);
                  }}
                >
                  <Box as={Stack} justifyContent="center">
                    {isPending && (
                      <Box mr={1}>
                        <Spinner size={16} fill={false} />
                      </Box>
                    )}
                    {t('Continue with workspace')}&nbsp;<em>{rememberedWorkspace}</em>
                  </Box>
                </Button>
                <Box style={{ display: 'flex', alignItems: 'center', width: '100%', margin: '28px 0' }}>
                  <Box bg={ThemeColors.Border} style={{ flexGrow: 1, height: '1px' }} />
                  <Box style={{ padding: '0 8px' }}>
                    <SmallSubtleText>{t('or use a different workspace')}</SmallSubtleText>
                  </Box>
                  <Box bg={ThemeColors.Border} style={{ flexGrow: 1, height: '1px' }} />
                </Box>
              </>
            )}
            <form style={{ width: '100%' }} onSubmit={onSubmit}>
              {rememberedWorkspace ? (
                <Box mb={3}>{workspaceIdentifierInput}</Box>
              ) : (
                <FormRow label={t('Sign in to your workspace')} dataKey="username">
                  {workspaceIdentifierInput}
                </FormRow>
              )}
              <Stack itemSpacing="2" justifyContent="left">
                <LoginFormButton loading={isPending}>{t('Continue')}</LoginFormButton>
                <Button variant="secondary" onClick={onCancel}>
                  {t('Cancel')}
                </Button>
              </Stack>
            </form>
          </Box>
          <Stack justifyContent="center" style={{ margin: 'auto 0 10px 0' }}>
            <Button variant="link" onClick={handleFindWorkspaces}>
              <SmallSubtleText>{t("Don't know your workspace?")}</SmallSubtleText>
              &nbsp;
              {t('Find your workspace')}
            </Button>
          </Stack>
        </Box>
      </TaniumLoginCard>
    </Box>
  );
};
